/**
 * T061: Prompt Detail Page
 * Full page view for a single prompt
 */

import { useState } from 'react'
import { useNavigate, Link } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { usePrompt } from '@/hooks/usePrompt'
import { useDeletePrompt } from '@/hooks/useDeletePrompt'
import { Button } from '@/components/ui/button'
import { PromptDetail } from '@/components/prompts/prompt-detail'
import { PromptDetailSkeleton } from '@/components/prompts/prompt-detail-skeleton'
import { ErrorMessage } from '@/components/common/error-message'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface PromptDetailPageProps {
  promptId: string
}

export function PromptDetailPage({ promptId }: PromptDetailPageProps) {
  const navigate = useNavigate()
  const { data: prompt, isLoading, error, refetch } = usePrompt(promptId)
  const deletePrompt = useDeletePrompt()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const handleDelete = () => {
    deletePrompt.mutate(promptId, {
      onError: () => {
        setShowDeleteDialog(false)
      },
    })
  }

  const handleEdit = () => {
    navigate({ to: '/prompts/$id/edit', params: { id: promptId } })
  }

  if (isLoading) {
    return <PromptDetailSkeleton />
  }

  if (error) {
    return (
      <ErrorMessage
        title="Failed to load prompt"
        message={error.message}
        onRetry={refetch}
      />
    )
  }

  if (!prompt) {
    return (
      <ErrorMessage
        title="Prompt not found"
        message="The prompt you're looking for doesn't exist or has been deleted."
      />
    )
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/prompts">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Prompts
        </Link>
      </Button>

      <PromptDetail
        prompt={prompt}
        onDelete={() => setShowDeleteDialog(true)}
        onEdit={handleEdit}
      />

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Prompt</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{prompt.title}"? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deletePrompt.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deletePrompt.isPending}
            >
              {deletePrompt.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
